import React from 'react';
import { Avatar, Chip, Spinner } from '@heroui/react';
import { IdView, Typography, CustomTooltip } from '../../../../components';
import AccountTable from '../../../../components/AccountTable';
import CustomHeroButton from '../../../../components/button';
import { useTranslations } from '../../../../contexts/translation.context';
import { useAuth } from '../../../../contexts/AuthContext';
import { useNavigate } from 'react-router';
import { MyOffersTableProps, MyOfferData } from '../types';
import { formatPrice, getStatusColor, getStatusDotColor } from '../utils';
import {
  CalendarIcon,
  DollarRoundedIc,
  GroupUserIc,
  InfoCircle,
} from '../../../../components/icons';
import EmptyState from './EmptyState';
import { getPlatformIcon, transformPlatform, getFilterIcon } from '../../../../utils';
import { formatTimeAgo } from '../../../../utils/formatTimeAgo';

const MyOffersTable: React.FC<MyOffersTableProps> = ({
  data,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslations();
  const { user } = useAuth();
  const navigate = useNavigate();
  const columns = [
    { name: t('salesFans.tableColumns.id'), key: 'id' },
    { name: t('salesFans.tableColumns.marketer'), key: 'marketer' },
    { name: t('salesFans.tableColumns.offer'), key: 'offer' },
    { name: t('salesFans.tableColumns.description'), key: 'description' },
    { name: t('salesFans.tableColumns.status'), key: 'status' },
    { name: t('salesFans.tableColumns.actions'), key: 'actions' },
  ];
  const isSubscriptionOffer = (item: MyOfferData) => item.offerType === 'subscription';

  const formatFansRange = (min: number, max: number | null, perDay: number): any => {
    const range = max ? `${min}-${max}` : `${min}+`;
    return (
      <span>
        {range}{' '}
        <Typography variant="body3" className="text-auth-muted">
          /~{perDay} {t('salesFans.tableData.perDay')}
        </Typography>
      </span>
    );
  };

  const getPartnerLevelStyle = (lvl: string) => {
    switch (lvl) {
      case 'expert':
        return {
          bg: 'bg-accent/20',
          text: 'text-accent',
          label: t('salesFans.tableData.partnerLevels.expert'),
        };
      case 'pro':
        return {
          bg: 'bg-auth-ink',
          text: 'text-white',
          label: t('salesFans.tableData.partnerLevels.pro'),
        };
      case 'new':
        return {
          bg: 'bg-success/20',
          text: 'text-success',
          label: t('salesFans.tableData.partnerLevels.new'),
        };
      default:
        return {
          bg: 'bg-[#EEEEEF]',
          text: 'text-auth-muted',
          label: t('salesFans.tableData.partnerLevels.base'),
        };
    }
  };

  const getTooltipText = (tag: string) => {
    switch (tag.toLowerCase()) {
      case 'free':
        return t('salesFans.freeToolTip');
      case 'free-trial':
        return t('salesFans.freeTrialToolTip');
      case 'paid':
        return t('salesFans.paidToolTip');
      default:
        return null;
    }
  };
  const renderCell = (item: MyOfferData, columnKey: string) => {
    switch (columnKey) {
      case 'id':
        return <IdView textToCopy={item.id}>{item.id.slice(-4)}</IdView>;

      case 'marketer':
        const partnerLevel = getPartnerLevelStyle(user?.lvl || 'base');
        return (
          <div className="flex items-center gap-4">
            {/* <div className="w-10 h-10 bg-gray-200 rounded-3 flex items-center justify-center">
              <span className="text-sm font-medium text-gray-600">
                {user?.name?.charAt(0) || 'M'}
              </span>
            </div> */}
            <Avatar
              src={user?.avatar}
              alt={user?.name || t('salesFans.labels.marketer')}
              showFallback
              name={user?.name?.slice(0, 1)}
              size="sm"
              className="w-10 h-10 rounded-3"
            />
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <Typography variant="body2" className="text-gray-900 font-medium">
                  {user?.name || t('salesFans.labels.marketer')}
                </Typography>
                <span
                  className={`px-2 py-1 rounded-full !capitalize !h-6 text-xs font-regular ${partnerLevel.bg} ${partnerLevel.text}`}
                >
                  {partnerLevel.label}
                </span>
              </div>
              <div className="flex items-center gap-1 text-gray-500 text-sm">
                <CalendarIcon />
                <CustomTooltip
                  content={
                    <div className="flex flex-col">
                      <Typography variant="body3" className="text-auth-muted">
                        {t('salesFans.tableData.updatedAt')}:
                      </Typography>
                      <Typography variant="body2">{formatTimeAgo(item.lastUpdated, t)}</Typography>
                    </div>
                  }
                  placement="top"
                  className="max-w-[18.75rem] break-all rounded-2 py-2 !px-4"
                >
                  <Typography variant="body3" className="text-auth-muted">
                    {formatTimeAgo(item.lastUpdated, t)}
                  </Typography>
                </CustomTooltip>
              </div>
            </div>
          </div>
        );

      case 'offer':
        return (
          <div className="flex flex-col gap-1 items-start">
            <div className="flex items-center gap-1 px-2 py-1 border !h-6 border-[1.4px] border-auth-strength-none rounded-xl">
              {item.source === 'another' ? (
                <Typography variant="body3">
                  {item.other_source_name || t('salesFans.labels.otherSource')}
                </Typography>
              ) : (
                <>
                  {getPlatformIcon(item.source)}
                  <Typography variant="body3">{transformPlatform(item.source)}</Typography>
                </>
              )}
              {item.description && (
                <>
                  <div className="w-[1px] h-3 bg-auth-strength-none mx-1"></div>
                  <Typography variant="body3">{item?.of_models}</Typography>
                  <CustomTooltip
                    content={item.description}
                    placement="top"
                    className="max-w-[18.75rem] break-all rounded-2 py-2 !px-4"
                  >
                    <div className="cursor-pointer flex items-center">
                      <InfoCircle />
                    </div>
                  </CustomTooltip>
                </>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <div className="flex items-center gap-1 flex-shrink-0 whitespace-nowrap">
                  <div className="min-w-3 max-w-3 flex-shrink-0">
                    <DollarRoundedIc />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                    {formatPrice(item.price.amount)}
                  </span>
                </div>
                <Typography variant="body3" className="text-auth-muted whitespace-nowrap">
                  {isSubscriptionOffer(item)
                    ? t('salesFans.tableData.perMonthPrice')
                    : t('salesFans.tableData.perFan')}
                </Typography>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {/* У офферов-подписок нет диапазона "всего подписчиков" — только объём в месяц. */}
                {isSubscriptionOffer(item) ? (
                  <div className="flex items-center gap-1 flex-shrink-0 whitespace-nowrap">
                    <div className="min-w-3 max-w-3 flex-shrink-0">
                      <GroupUserIc />
                    </div>
                    <Typography variant="body3" className="text-auth-muted whitespace-nowrap">
                      ~{item.fansRange.perDay} {t('salesFans.tableData.perMonth')}
                    </Typography>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-1 flex-shrink-0 whitespace-nowrap">
                      <div className="min-w-3 max-w-3 flex-shrink-0">
                        <GroupUserIc />
                      </div>
                      <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                        {item.fansRange.min}-{item.fansRange.max || '∞'}
                      </span>
                    </div>
                    <Typography variant="body3" className="text-auth-muted whitespace-nowrap">
                      /~{item.fansRange.perDay} {t('salesFans.tableData.perDay')}
                    </Typography>
                  </>
                )}
              </div>
            </div>
          </div>
        );

      case 'description':
        return (
          <div className="flex flex-col gap-1">
            <div className="flex gap-1 items-center">
              {item.tags.map((tag: string, index: number) => {
                const tooltipText = getTooltipText(tag);
                const TagComponent = (
                  <Typography
                    variant="label3"
                    key={index}
                    className={`${tag === 'free' ? '!uppercase' : '!capitalize'} font-semibold`}
                  >
                    {tag}
                  </Typography>
                );

                return tooltipText ? (
                  <CustomTooltip
                    key={index}
                    content={tooltipText}
                    placement="top"
                    className="w-[18.75rem] md:w-[27.25rem] break-all rounded-2 py-2 !px-4"
                  >
                    {TagComponent}
                  </CustomTooltip>
                ) : (
                  TagComponent
                );
              })}
              <CustomTooltip
                content={item?.requirements || t('proposals.noRequirements')}
                placement="top"
                className="max-w-[18.75rem] break-all rounded-2 py-2 !px-4"
              >
                <div className="flex items-center gap-1 px-1.5 py-0.5 bg-neutral-12 rounded-full cursor-pointer hover:bg-[#e0e0e0] transition-colors">
                  <span className="text-xs font-regular text-auth-muted">
                    {t('proposals.requirements')}
                  </span>
                  <InfoCircle />
                </div>
              </CustomTooltip>
            </div>
            <div className="flex flex-col  gap-1">
              <div className="flex items-center gap-1">
                <div className="size-[0.4375rem] bg-success rounded-full"></div>
                <Typography variant="body3" className="text-auth-muted">
                  {t('salesFans.tableData.active')}:{' '}
                  <Typography variant="label3">{item.orders.active}</Typography>
                </Typography>
              </div>
              <div className="flex items-center gap-1">
                <div className="size-[0.4375rem] bg-partner rounded-full"></div>
                <Typography variant="body3" className="text-auth-muted">
                  {t('salesFans.tableData.completed')}:{' '}
                  <Typography variant="label3">{item.orders.completed}</Typography>
                </Typography>
              </div>
              <div className="flex items-center gap-1">
                <div className="size-[0.4375rem] bg-neutral-500 rounded-full"></div>
                <Typography variant="body3" className="text-auth-muted">
                  {t('salesFans.tableData.cancelled')}:{' '}
                  <Typography variant="label3">{item.orders.cancelled}</Typography>
                </Typography>
              </div>
            </div>
          </div>
        );

      case 'status':
        const statusText = (() => {
          if (item.status === 'active') return t('salesFans.tableData.status.active');
          if (item.status === 'inactive') return t('salesFans.tableData.status.inactive');
          if (item.status === 'pending') return t('salesFans.tableData.status.pending');
          if (item.status === 'moderation') return t('salesFans.tableData.status.moderation');
          if (item.status === 'hidden') return t('salesFans.tableData.status.deactivated');
          return item.status;
        })();

        return (
          <Chip
            size="sm"
            className="bg-auth-surface flex items-center px-2 gap-1 rounded-2"
            startContent={
              <div
                className={`size-[0.4375rem] rounded-full ${getStatusDotColor(item.status as any)}`}
              ></div>
            }
          >
            {statusText}
          </Chip>
        );

      case 'actions':
        return (
          <div className="flex flex-col gap-2 w-[9.75rem]">
            <CustomHeroButton
              title={t('salesFans.edit')}
              onPress={() => navigate(`/partner/subscribers/sales/${item.id}/edit`)}
              className="w-full rounded-2 h-8 !text-xs"
              customStyles={{ fontWeight: 400 }}
            />
            {/* <Snippet
              symbol={null}
              codeString={item.id}
              tooltipProps={{
                color: "foreground",
                content: t('salesFans.copyOfferId'),
                disableAnimation: true,
                placement: "right",
                closeDelay: 0,
              }}
              classNames={{
                base: "bg-[#E2EBF7] rounded-2 w-full px-2 h-8 gap-0 py-2",
                pre: "bg-transparent text-partner text-xs border-none",
                copyButton: "text-partner !min-w-[1.375rem] !max-w-4 h-4 hover:text-partner",
                checkIcon: "text-partner"
              }}
            >
              {t('salesFans.copyOffer')}
            </Snippet> */}
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner
          size="lg"
          classNames={{
            circle1: '!border-b-partner',
            circle2: '!border-b-partner/50',
          }}
        />
      </div>
    );
  }

  return (
    <AccountTable
      data={data}
      columns={columns}
      renderCell={renderCell}
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      currentPage={currentPage}
      currentPageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      emptyContent={
        // <EmptyState title={t('salesFans.noData')} />
        <EmptyState
          title={t('salesFans.noOffers')}
          description={t('salesFans.noOffersCategoryDescription')}
        />
      }
    />
  );
};

export default MyOffersTable;
